import { Inject, Injectable } from '@angular/core';

import { EnumTranslatorBase, PaymentStatus } from '@skylab-shared/dataModel';

import { EInvoiceReceivedGridItem, PurchaseGridItem } from '@skylab-angular-shared/business';
import { EnumTranslator, L10n, L10nBase } from '@skylab-angular-shared/core';
import { DialogInsService } from '@skylab-angular-shared/ui';
import { PurchaseServicePaymentDialogComponent, isEInvoicesGridItem } from '../../dialogs/purchase-service-payment/purchase-service-payment-dialog.component';

type Entity = PurchaseGridItem | EInvoiceReceivedGridItem;

@Injectable({providedIn: 'root'})
export class PurchaseServicePaymentDataProvider {
    public constructor(
        private readonly dialogService: DialogInsService,
        @Inject(EnumTranslator) private readonly enumTranslator: EnumTranslatorBase,
        @Inject(L10n) private readonly l10n: L10nBase
    ) {}

    public getPaymentStatus(entity: Entity): PaymentStatus | undefined {
        if (!entity.dueDate) {
            return entity.leftToPay === 0 ? PaymentStatus.Paid : PaymentStatus.Unpaid;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const paymentDate = new Date(entity.dueDate);
        paymentDate.setHours(0, 0, 0, 0);

        if (entity.grossValue === 0 || entity.leftToPay === 0) {
            return PaymentStatus.Paid;
        } else if (paymentDate >= today && entity.leftToPay !== 0) {
            return PaymentStatus.Unpaid;
        } else if (paymentDate < today) {
            return PaymentStatus.Overdue;
        }

        return undefined;
    }

    public isDocumentPaid(entity: Entity): boolean {
		const status = this.getPaymentStatus(entity);
		return status === PaymentStatus.Paid;
    }

    public openPaymentDialog(entity: Entity): void {
        if (this.isDocumentPaid(entity)) return;

        this.dialogService.showAsync({
            component: PurchaseServicePaymentDialogComponent,
            componentProps: {
                data: {
                    entity,
                    onSave: async (data: { version: string; leftToPay: number }): Promise<void> => {
                        entity.leftToPay = data.leftToPay;
                    }
                }
            }
        });
    }

    public getPaymentStatusText(entity: Entity): string {
        if (isEInvoicesGridItem(entity) && !entity.documentNumber) {
            return this.l10n._T('scenes.e-invoices-received.service.grid.columns.payment-status.not-applicable');
        }

		const status = this.getPaymentStatus(entity);
        return status ? this.enumTranslator.getLabeledEnumValue(PaymentStatus, status)?.label || '' : '';
    }
}
